import React, { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Check, RefreshCw } from 'lucide-react';
import { AI_MODELS } from '../../constants/models';

import styles from './styles/ModelSelector.module.css';

export const ModelDropdown = ({
    selectedModel,
    onModelSelect,
    position = 'top',
    isRegenerate = false,
    availableModels = null,
}) => {
    const dropdownRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(null);
    const [leftPosition, setLeftPosition] = useState(null);
    const [isRendered, setIsRendered] = useState(false);

    const updateDropdownPosition = useCallback(() => {
        const margin = 25;
        if (dropdownRef.current) {
            // Vertical position
            if (position === 'top') {
                const dropdownRect = dropdownRef.current.getBoundingClientRect();
                const spaceAbove = dropdownRect.bottom - margin;
                setMaxHeight(spaceAbove);
            } else {
                const dropdownRect = dropdownRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - dropdownRect.top - margin;
                setMaxHeight(spaceBelow);
            }

            // Horizontal position
            const dropdownRect = dropdownRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;

            // If dropdown overflows on the right
            if (dropdownRect.right > viewportWidth - margin) {
                const overflowRight = dropdownRect.right - (viewportWidth - margin);
                setLeftPosition(-overflowRight);
            } 
            // If dropdown overflows on the left
            else if (dropdownRect.left < margin) {
                setLeftPosition(margin - dropdownRect.left);
            } else {
                setLeftPosition(null);
            }
        }
    }, [position]);

    // Mark component as rendered after first mount
    useEffect(() => {
        setIsRendered(true);
    }, []);

    // Initial position calculation with a delay to ensure proper rendering
    useEffect(() => {
        if (isRendered && dropdownRef.current) {
            // Small delay to ensure the DOM is fully rendered with correct dimensions
            const timer = setTimeout(() => {
                updateDropdownPosition();
            }, 10);
            return () => clearTimeout(timer);
        }
    }, [isRendered, updateDropdownPosition]);

    // Update on resize
    useEffect(() => {
        const handleResize = () => {
            updateDropdownPosition();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateDropdownPosition]);

    const filteredModels = useMemo(() => {
        const models = {};
        Object.entries(AI_MODELS).forEach(([key, provider]) => {
            models[key] = {
                ...provider,
                models: provider.models.map(model => ({
                    ...model,
                    isDisabled: availableModels ? !availableModels.includes(model.id) : false,
                    disabledReason: model.isImageSupported ? null :
                        "This model doesn't support image attachments",
                })),
            };
        });
        return models;
    }, [availableModels]);

    return (
        <motion.div
            ref={dropdownRef}
            className={`${styles.dropdown} ${styles[position]}`}
            style={{
                maxHeight: maxHeight ? `${maxHeight}px` : 'none',
                left: leftPosition !== null ? `${leftPosition}px` : '-1rem',
                animation: 'none',
                transformOrigin: position === 'top' ? 'bottom left' : 'top left',
            }}
            initial={{ opacity: 0, y: position === 'top' ? -8 : 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: position === 'top' ? -6 : 6, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30, mass: 0.2 }}
        >
            {Object.entries(filteredModels).map(([key, provider]) => (
                <div key={key} className={styles.providerSection}>
                    {Object.keys(filteredModels).length > 1 && (
                        <div className={styles.providerName}>{provider.name}</div>
                    )}
                    {provider.models.map(model => (
                        <div key={model.id} className={styles.modelOptionContainer}>
                            <button
                                className={`${styles.modelOption} ${model.isDisabled ? styles.disabled : ''}`}
                                onClick={() => !model.isDisabled && onModelSelect(model.id)}
                            >
                                <div className={styles.modelInfo}>
                                    <span className={styles.modelLabel}>{model.label}</span>
                                    <span className={styles.modelDescription}>
                                        {model.description}
                                        {model.isRecommended && <b> (recommended)</b>}
                                    </span>
                                </div>
                                {selectedModel === model.id && !isRegenerate && (
                                    <div className={styles.checkIcon}>
                                        <Check />
                                    </div>
                                )}
                                {selectedModel === model.id && isRegenerate && (
                                    <div className={styles.regenerateIcon}>
                                        <RefreshCw />
                                    </div>
                                )}
                            </button>
                            {model.isDisabled && (
                                <span className={styles.tooltip}>{model.disabledReason}</span>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </motion.div>
    );
};

const ModelSelector = ({ selectedModel, onModelSelect }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getCurrentModelLabel = useCallback(modelId => {
        for (const provider of Object.values(AI_MODELS)) {
            const model = provider.models.find(m => m.id === modelId);
            if (model) return model.label;
        }
        return 'model';
    }, []);

    return (
        <div className={styles.container} ref={containerRef}>
            <button
                className={`${styles.selector} ${isOpen ? styles.selected : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Select AI model"
            >
                <span className={styles.currentModel}>
                    {getCurrentModelLabel(selectedModel)}
                </span>
                <ChevronDown size={13} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <ModelDropdown 
                        selectedModel={selectedModel}
                        onModelSelect={modelId => {
                            onModelSelect(modelId);
                            setIsOpen(false);
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default memo(ModelSelector);
